import {
  CheckCircleFilled,
  DownOutlined,
  FireFilled,
  UpOutlined,
} from '@ant-design/icons'
import { Form, message, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
  getTodayTasks,
  switchTaskCheckList,
  switchFastlaneCheckList,
} from '../../api/aops.api'
import { userState } from '../../store/user'
import BoardWorkPopup from './BoardWorkPopup'
import InitiativeBoardWorkPopup from './InitiativeBoardWorkPopup'
import ObjectiveDetailPopup from './ObjectiveDetailPopup'
import './initiativeKanbanBoard.scss'
dayjs.locale('ko')
const { Text } = Typography

const mapProgressStateAndPriorityToFrontendColor = (
  progressState,
  priority,
) => {
  const mapInProgressPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FF7853'
      case 'SECOND':
        return '#FFCA00'
      case 'THIRD':
        return '#1FC297'
      default:
        return ''
    }
  }
  const mapTodoPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FFCCB5'
      case 'SECOND':
        return '#FFF0B5'
      case 'THIRD':
        return '#D9FCED'
      default:
        return ''
    }
  }
  switch (progressState) {
    case 'TODO':
      return mapTodoPriorityToFrontendColor(priority)
    case 'IN_PROGRESS':
      return mapInProgressPriorityToFrontendColor(priority)
    case 'IN_REVIEW':
      return mapInProgressPriorityToFrontendColor(priority)
    case 'DONE':
      return mapTodoPriorityToFrontendColor(priority)
    case 'ON_HOLD':
      return mapTodoPriorityToFrontendColor(priority)
    default:
      return ''
  }
}

const mapPriorityToFrontendColor = (priority) => {
  switch (priority) {
    case 'FIRST':
      return '#FF7853'
    case 'SECOND':
      return '#FFCA00'
    case 'THIRD':
      return '#1FC297'
    default:
      return ''
  }
}

const mapProgressStateToOpacity = (progressState) => {
  switch (progressState) {
    case 'TODO':
      return 1
    case 'IN_PROGRESS':
      return 1
    case 'IN_REVIEW':
      return 1
    case 'DONE':
      return 0.5
    case 'ON_HOLD':
      return 0.5
    default:
      return 0.5
  }
}

const ScrumBoard = ({
  tasks = [],
  activeLevel,
  createType,
  selectedTeam,
  onReloadList,
  onTaskClick,
  userId,
  agileBoardData,
  transformInitiatives = () => ({}),
  showCreateButton = true,
  onOpenStoryPopup,
  onOpenStoryCreate,
  onInitiativePopupOpenChange,
}) => {
  /** [추가] 칸반보드에서 그대로 가져온 상태와 핸들러 */
  const [user] = useRecoilState(userState)
  const [form] = Form.useForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  // ObjectiveDetailPopup에 표시할 objective 상태 (스택 유지를 위해 별도 관리)
  const [selectedObjectiveForDetail, setSelectedObjectiveForDetail] =
    useState(null)
  // InitiativeBoardWorkPopup이 열려있는지 추적 (Task 팝업 z-index 계산용)
  const [isInitiativePopupOpen, setIsInitiativePopupOpen] = useState(false)
  // ObjectiveDetailPopup에서 Initiative를 클릭했는지 추적 (type을 STORY로 설정하기 위함)
  const [isOpeningStoryFromObjective, setIsOpeningStoryFromObjective] =
    useState(false)

  const GRID_COLS = 5
  const COL_WIDTH_PERCENT = 100 / GRID_COLS // 20

  /** [추가] 오늘 진행 업무 전용 상태 */
  const [todayCheckList, setTodayCheckList] = useState([])

  const [openTaskIndex, setOpenTaskIndex] = useState(0)

  /** [추가] 오늘 진행 업무 API 호출 */
  const fetchTodayData = async () => {
    try {
      const res = await getTodayTasks(userId) // 컨트롤러 명세에 따라 호출
      if (res.data) {
        setTodayCheckList(res.data)
      }
      // console.log('todayCheckList', todayCheckList)
    } catch (error) {
      console.log('오늘의 업무 로드 실패:', error)
    }
  }
  /** [새로 추가] 하위 업무 체크 상태 토글 핸들러 */
  const handleToggleCheck = async (checkListId, isFast = false) => {
    try {
      // FastLane과 Task에 따라 다른 API 호출
      if (isFast) {
        await switchFastlaneCheckList(checkListId)
      } else {
        await switchTaskCheckList(checkListId)
      }
      // 1. 타임 테이블 데이터 갱신
      await fetchTodayData()
      // 2. 부모 컴포넌트(보드) 데이터 갱신 (진행률 카운트 업데이트용)
      if (onReloadList) onReloadList()
    } catch (error) {
      console.error('체크 상태 변경 실패:', error)
      message.error('상태 변경에 실패했습니다.')
    }
  }

  // console.log('tasks', tasks)

  useEffect(() => {
    if (userId !== null && userId !== undefined) {
      fetchTodayData()
    }
  }, [userId])

  // 보드 데이터 갱신 시 타임 테이블도 같이 갱신
  useEffect(() => {
    if (onReloadList) {
      fetchTodayData()
    }
  }, [tasks])

  const today = dayjs().format('YYYY-MM-DD')
  const todayTasks = tasks.filter(
    (t) => dayjs(t.worksInfoDto.startDateTime).format('YYYY-MM-DD') === today,
  )

  const handleLoadTaskDetail = async (task) => {
    setSelectedTask(task)

    // EPIC(Objective) 카드 클릭 시 즉시 selectedObjectiveForDetail 설정
    if (createType === 'EPIC' && agileBoardData?.objectives) {
      const objective = agileBoardData.objectives.find(
        (o) => o.id === (task?.worksInfoDto?.id ?? task?.id),
      )
      if (objective) {
        // ObjectiveDetailPopup을 열기 위해 selectedObjectiveForDetail 설정
        // isModalOpen은 false로 유지하여 BoardWorkPopup이 열리지 않도록 함
        setSelectedObjectiveForDetail(objective)
        return // BoardWorkPopup은 열지 않음
      }
    }

    // 일반 카드(STORY/TASK) 클릭 시 BoardWorkPopup 열기
    setIsModalOpen(true)
  }

  const handleCancel = () => {
    // BoardWorkPopup만 닫기 (ObjectiveDetailPopup은 유지)
    setIsModalOpen(false)
    setSelectedTask(null)
    setIsInitiativePopupOpen(false) // InitiativeBoardWorkPopup 닫기
    setIsOpeningStoryFromObjective(false) // ObjectiveDetailPopup에서 열었는지 플래그 초기화
    if (onInitiativePopupOpenChange) onInitiativePopupOpenChange(false)
    form.resetFields()
    // selectedObjectiveForDetail은 유지하여 ObjectiveDetailPopup이 계속 열려있도록 함
    if (onReloadList) onReloadList()
  }

  const getFilteredWeekTasks = (serverData, weekStartMonday) => {
    // 이번 주 금요일 종료 시점 계산 (월요일 + 4일)
    const weekEndFriday = weekStartMonday.add(4, 'day').endOf('day')

    return serverData
      .filter((item) => {
        if (!item?.worksInfoDto) return false

        const taskStart = dayjs(item.worksInfoDto.startDateTime)
        const taskEnd = dayjs(item.worksInfoDto.endDateTime)

        // [수정] 이번 주 월~금 범위 내에 조금이라도 걸쳐 있는 작업만 필터링
        return (
          taskStart.isBefore(weekEndFriday) &&
          taskEnd.isAfter(weekStartMonday.subtract(1, 'ms'))
        )
      })
      .map((item) => {
        const info = item.worksInfoDto
        const taskStart = dayjs(info.startDateTime)
        const taskEnd = dayjs(info.endDateTime)

        // [계산] 화면에 실제 표시될 시작/종료일 계산 (월~금 범위를 벗어나면 절삭)
        const displayStart = taskStart.isBefore(weekStartMonday)
          ? weekStartMonday
          : taskStart
        const displayEnd = taskEnd.isAfter(weekEndFriday)
          ? weekEndFriday
          : taskEnd

        // [계산] 그리드 상의 위치(startCol)와 길이(span) 계산
        const startCol = displayStart.diff(weekStartMonday, 'day')
        const span = displayEnd.diff(displayStart, 'day') + 1

        // [추가] 시각적 연속성 표현을 위한 플래그 계산
        // 실제 시작일이 이번 주 월요일보다 이전인가? -> 왼쪽이 잘린 느낌 필요
        const isContinuedFromLastWeek = taskStart.isBefore(weekStartMonday)
        // 실제 종료일이 이번 주 금요일보다 이후인가? -> 오른쪽이 잘린 느낌 필요
        const isContinuedToNextWeek = taskEnd.isAfter(weekEndFriday)

        return {
          ...item,
          startCol,
          span,
          // [추가] 플래그 전달
          isContinuedFromLastWeek,
          isContinuedToNextWeek,
          color: mapProgressStateAndPriorityToFrontendColor(
            info.progressState,
            info.priority,
          ),
          hasIssue: item.hasIssue,
          opacity: mapProgressStateToOpacity(info.progressState),
        }
      })
  }

  const getWeekInfo = (weekOffset = 0) => {
    const monday = dayjs()
      .startOf('week')
      .add(1, 'day')
      .add(weekOffset, 'week')
      .startOf('day')
    const today = dayjs().format('YYYY-MM-DD')
    const days = [...Array(5)].map((_, i) => {
      const day = monday.add(i, 'day')
      const fullDate = day.format('YYYY-MM-DD')
      return {
        date: day.format('M/D'),
        dayName: day.format('ddd'),
        fullDate,
        isToday: fullDate === today,
      }
    })
    return { monday, days }
  }

  const thisWeek = getWeekInfo(0)
  const nextWeek = getWeekInfo(1)

  const renderWeekRow = (weekInfo, isLast = false) => {
    const { monday, days } = weekInfo
    const weekTasks = getFilteredWeekTasks(tasks, monday)

    return (
      <div
        style={{
          marginBottom: isLast ? 0 : 8,
          borderBottom: '1px solid #Eef3f6',
        }}
      >
        {/* 1. 주간 헤더 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)', // 7 -> 5
            textAlign: 'center',
            background: '#F6F7FA',
            borderRadius: '8px',
            marginBottom: '16px',
            position: 'relative',
            zIndex: 2,
            border: '1px solid #Eef3f6',
          }}
        >
          {days.map((d) => (
            <div
              key={d.fullDate}
              style={{
                padding: '12px 0',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: d.isToday ? '#383E44' : 'transparent',
                }}
              >
                <Text
                  style={{
                    fontSize: '16px',
                    color: d.isToday ? '#FFFFFF' : '#191A1C',
                    fontWeight: '700',
                  }}
                >
                  {d.date}
                </Text>
                <Text
                  style={{
                    fontSize: '16px',
                    color: d.isToday ? '#FFFFFF' : '#6d747a',
                    fontWeight: '500',
                  }}
                >
                  {d.dayName}
                </Text>
              </div>
            </div>
          ))}
        </div>

        {/* 2. 보드 본문 영역 */}
        <div
          style={{
            position: 'relative',
            minHeight: '300px', // [수정] 선이 충분히 길게 내려오도록 높이 대폭 상향
            paddingBottom: '40px', // 하단 여백 추가
          }}
        >
          {/* [배경 레이어] 그리드 및 오늘 날짜 세로선 */}
          <div
            style={{
              position: 'absolute',
              top: -16,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)', // 7 -> 5
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {days.map((d, i) => (
              <div
                key={i}
                style={{
                  position: 'relative',
                  borderRight: i === 4 ? 'none' : '1px solid #F0F2F5', // 마지막 인덱스 4
                  height: '100%',
                }}
              >
                {/* {d.isToday && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -10,
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '1px', // [수정] 선을 더 명확하게 (1.5px -> 2px)
                      background: '#6D747A', // [수정] 진한 색상 유지
                      zIndex: 1,
                    }}
                  />
                )} */}
              </div>
            ))}
          </div>

          {/* [콘텐츠 레이어] 일정 카드들 */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            {weekTasks.map((task) => {
              // [핵심 수정] 연속성 플래그에 따라 동적으로 테두리 둥글기 스타일 결정
              const borderRadiusStyle = {
                // 지난주에서 이어지면 왼쪽은 직각(0px), 아니면 둥글게(12px)
                borderTopLeftRadius: task.isContinuedFromLastWeek
                  ? '0px'
                  : '12px',
                borderBottomLeftRadius: task.isContinuedFromLastWeek
                  ? '0px'
                  : '12px',
                // 다음주로 이어지면 오른쪽은 직각(0px), 아니면 둥글게(12px)
                borderTopRightRadius: task.isContinuedToNextWeek
                  ? '0px'
                  : '12px',
                borderBottomRightRadius: task.isContinuedToNextWeek
                  ? '0px'
                  : '12px',
              }

              return (
                <div
                  key={`${task.worksInfoDto.id}-${monday.format('YYYY-MM-DD')}`}
                  onClick={() => handleLoadTaskDetail(task)}
                  style={{
                    position: 'relative',
                    marginLeft: `${task.startCol * COL_WIDTH_PERCENT}%`, // [수정] 20% 기준
                    width: `${task.span * COL_WIDTH_PERCENT}%`, // [수정] 20% 기준
                    height: '40px',
                    background: '#FFFFFF',
                    boxShadow: '2px 2px 8px 0 rgba(204, 211, 215, 0.30)',

                    // [수정] 기본 borderRadius 제거하고 동적 스타일 적용
                    ...borderRadiusStyle,

                    // [수정] 이어지는 방향의 테두리 선을 제거하여 '잘린' 느낌 강조
                    borderLeft: task.isContinuedFromLastWeek
                      ? 'none'
                      : '1px solid #E2EAEF',
                    borderRight: task.isContinuedToNextWeek
                      ? 'none'
                      : '1px solid #E2EAEF',
                    borderTop: '1px solid #E2EAEF',
                    borderBottom: '1px solid #E2EAEF',

                    padding: '0 12px 0 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    opacity: task.opacity,
                    overflow: 'hidden',
                  }}
                >
                  {/* 1. 상단 프로그레스 바 영역 (이중 레이어 구조) */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      backgroundColor: '#E2EAEF', // 기본 배경색 (회색)
                      zIndex: 2,
                    }}
                  >
                    {createType === 'TASK' || createType === 'STORY' ? (
                      <div
                        style={{
                          // 진행률 계산: (완료된 업무 / 전체 업무) * 100
                          width: `${
                            task.totalChildCount > 0
                              ? (task.completedChildCount /
                                  task.totalChildCount) *
                                100
                              : 100
                          }%`,
                          height: '100%',
                          backgroundColor: task.color, // 채워지는 색상 (우선순위 기반 색상)
                          transition: 'width 0.3s ease', // 수치가 변할 때 부드럽게 채워짐
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: task.color,
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      minWidth: 0,
                      marginRight: '8px',
                    }}
                  >
                    <Text
                      strong
                      style={{
                        fontSize: '12px',
                        color: '#191A1C',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        marginTop: '4px',
                        flex: 1,
                      }}
                    >
                      {task.worksInfoDto.title}
                    </Text>
                  </div>
                  {(task.hasIssue ||
                    task.worksInfoDto.storyChildProblemCount > 0) && (
                    <Tag
                      icon={
                        <FireFilled
                          style={{
                            color:
                              task.worksInfoDto.problemState ===
                              'RESOLVED_OVERDUE_REVIEW_TIME'
                                ? '#6D747A'
                                : '#F25A67',
                          }}
                        />
                      }
                      style={{
                        borderRadius: '6px',
                        fontSize: '10px',
                        margin: 0,
                        background: '#fff',
                        color:
                          task.worksInfoDto.problemState ===
                          'RESOLVED_OVERDUE_REVIEW_TIME'
                            ? '#6D747A'
                            : '#F25A67',
                        border:
                          task.worksInfoDto.problemState ===
                          'RESOLVED_OVERDUE_REVIEW_TIME'
                            ? '1px solid #6D747A'
                            : '1px solid #F25A67',
                        fontWeight: '700',
                        padding: '6px 6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0px',
                      }}
                    ></Tag>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  /** [수정] 타임 테이블 렌더링 (TaskWorkCheckListItem DTO 기준) */
  const renderTaskTimeTable = () => {
    return (
      <div
        style={{
          flex: 1,
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '16px',
          // border: '1px solid #E2EAEF',
          height: 'fit-content',
          position: 'sticky',
          top: '20px',
          boxShadow: '0 2px 8px #e2eaef',
        }}
      >
        <div
          style={{
            marginBottom: '20px',
            backgroundColor: '#f6f7fa',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #Eef3f6',
          }}
        >
          <Text
            style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#191A1C',
              letterSpacing: '-0.35px',
            }}
          >
            업무 타임 테이블
          </Text>
          <div
            style={{
              gap: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                fontSize: '14px',
                color: '#383E44',
                letterSpacing: '-0.35px',
              }}
            >
              오늘 진행 업무
            </Text>
            <Text
              style={{
                fontSize: '16px',
                color: '#ced5db',
                letterSpacing: '-0.35px',
              }}
            >
              |
            </Text>
            <Text
              style={{ fontSize: '14px', color: '#007AFF', fontWeight: '700' }}
            >
              {todayCheckList.length}
            </Text>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {todayCheckList.map((item, idx) => {
            const hasCheckListDto =
              item?.tasksCheckListDto && item?.tasksCheckListDto?.length > 0
            const isOpen = openTaskIndex === idx && hasCheckListDto
            return (
              <div key={idx}>
                {/* 상단: 업무명 및 종료 시간 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                    cursor: hasCheckListDto ? 'pointer' : 'default',
                  }}
                  onClick={() =>
                    hasCheckListDto
                      ? setOpenTaskIndex(isOpen ? null : idx)
                      : null
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {/* DTO에는 우선순위가 없으므로 기본 파란색 바 적용 */}
                    <div
                      style={{
                        width: '4px',
                        height: '16px',
                        background: '#007AFF',
                        borderRadius: '2px',
                      }}
                    />
                    <Text
                      style={{
                        fontWeight: '700',
                        fontSize: '15px',
                        textDecoration: item.isDone ? 'line-through' : 'none',
                        color: item.isDone ? '#A4ADB4' : '#191A1C',
                        letterSpacing: '-0.35px',
                      }}
                    >
                      {item.emoji && `${item.emoji} `}
                      {item.taskName}
                    </Text>
                    {/* Fast 태그 */}
                    {item.isFast && (
                      <Tag
                        style={{
                          padding: '0 4px',
                          background: '#ECE6FF',
                          color: '#7D51FE',
                          borderRadius: '2px',
                          border: 'none',
                          fontWeight: '600',
                        }}
                      >
                        Fast
                      </Tag>
                    )}

                    {/* 하위 업무 리스트 열기/닫기 버튼 */}
                    {hasCheckListDto && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {isOpen ? (
                          <UpOutlined
                            style={{ fontSize: '12px', color: '#A4ADB4' }}
                          />
                        ) : (
                          <DownOutlined
                            style={{ fontSize: '12px', color: '#A4ADB4' }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <Text
                    style={{
                      fontSize: '12px',
                      color: '#8A949E',
                      letterSpacing: '-0.35px',
                    }}
                  >
                    {(() => {
                      const targetDate = dayjs(item.taskEndDateTime)
                      const isToday = targetDate.isSame(dayjs(), 'day')

                      // 오늘이면 HH:mm, 아니면 DD HH:mm 포맷 적용
                      return isToday
                        ? `${targetDate.format('HH:mm')} 까지`
                        : `${targetDate.format('M월 D일 HH:mm')} 까지`
                    })()}
                  </Text>
                </div>

                {/* 하위 업무 리스트 (tasksCheckListDto) */}
                {isOpen && (
                  <div
                    style={{
                      // background: '#F6F7FA',
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      border: '1px solid #Eef3f6',
                    }}
                  >
                    {item.tasksCheckListDto?.map((sub, idx) => {
                      const isLast = idx === item.tasksCheckListDto.length - 1
                      return (
                        <div
                          key={sub.id}
                          onClick={() => handleToggleCheck(sub.id, item.isFast)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s',
                            borderBottom: isLast ? 'none' : '1px solid #f6f7fa',
                            paddingBottom: '6px',
                          }}
                        >
                          {sub.isChecked ? (
                            <CheckCircleFilled style={{ color: '#383E44' }} />
                          ) : (
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #D1D5DB',
                                borderRadius: '50%',
                                background: '#fff',
                              }}
                            />
                          )}
                          <Text
                            style={{
                              fontSize: '14px',
                              color: sub.isChecked ? '#6d747a' : '#383E44',
                              textDecoration: sub.isChecked
                                ? 'line-through'
                                : 'none',
                              fontWeight: sub.isChecked ? '500' : '700',
                              letterSpacing: '-0.35px',
                            }}
                          >
                            {sub.emoji && `${sub.emoji} `}
                            {sub.title}
                          </Text>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          {todayCheckList.length === 0 && (
            <Text
              style={{
                textAlign: 'center',
                color: '#A4ADB4',
                padding: '20px 0',
                letterSpacing: '-0.35px',
              }}
            >
              오늘 예정된 업무가 없습니다.
            </Text>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
      {/* 1. 왼쪽 영역: 2주차 스크럼 보드 */}
      <div
        style={{
          flex: 2,
          background: '#fff',
          borderRadius: '16px',
          padding: '16px',
          boxShadow: '0 2px 8px #e2eaef',
        }}
      >
        {createType !== 'STORY' && (
          <>
            {createType === 'EPIC' && (
              <ObjectiveDetailPopup
                open={!!selectedObjectiveForDetail}
                onClose={() => {
                  setSelectedObjectiveForDetail(null) // ObjectiveDetailPopup 닫기
                  setIsModalOpen(false)
                  setSelectedTask(null)
                  setIsOpeningStoryFromObjective(false) // ObjectiveDetailPopup에서 열었는지 플래그 초기화
                  if (onReloadList) onReloadList()
                }}
                objective={selectedObjectiveForDetail ?? null}
                objectiveNumber={
                  selectedObjectiveForDetail
                    ? String(selectedObjectiveForDetail.sequence ?? 1).padStart(
                        2,
                        '0',
                      )
                    : '01'
                }
                objectiveTitle={selectedObjectiveForDetail?.title ?? ''}
                tasksByCategory={transformInitiatives(
                  selectedObjectiveForDetail?.initiativesByCategory ?? {},
                )}
                onTaskClick={(task) => {
                  // Initiative 카드 클릭 시: ScrumBoard 내부의 BoardWorkPopup 열기
                  // SchedulePage의 BoardWorkPopup이 아닌 ScrumBoard 내부의 BoardWorkPopup을 사용하여
                  // 같은 컴포넌트 내에서 렌더링되도록 하여 z-index가 올바르게 작동하도록 함
                  // InitiativeKanbanBoard에서 전달되는 task는 initiative 객체이므로 worksInfoDto 형식으로 변환
                  const formattedTask = task?.worksInfoDto
                    ? task
                    : {
                        worksInfoDto: {
                          id: task?.id,
                          title: task?.title,
                        },
                        id: task?.id,
                      }
                  setSelectedTask(formattedTask)
                  setIsOpeningStoryFromObjective(true) // ObjectiveDetailPopup에서 Initiative 클릭 표시
                  setIsModalOpen(true)
                }}
                onCreateClick={() => {
                  // Initiative 업무 생성 버튼 클릭 시: ScrumBoard 내부의 BoardWorkPopup 열기
                  setSelectedTask(null)
                  setIsOpeningStoryFromObjective(true) // ObjectiveDetailPopup에서 Initiative 생성 표시
                  setIsModalOpen(true)
                }}
                onObjectiveClick={(obj) => {
                  // "상세 보기" 클릭 시: ObjectiveDetailPopup은 유지하고 BoardWorkPopup만 열기
                  setSelectedTask({
                    worksInfoDto: { id: obj.id, title: obj.title },
                    id: obj.id,
                  })
                  setIsModalOpen(true) // BoardWorkPopup 열기 (ObjectiveDetailPopup은 그대로 유지)
                }}
                showCreateButton={showCreateButton}
                zIndex={1000} // 기본 레벨, BoardWorkPopup(1100)이 위에 쌓임
              />
            )}
            <BoardWorkPopup
              open={isModalOpen}
              onClose={handleCancel}
              zIndex={
                isInitiativePopupOpen
                  ? 1200
                  : selectedObjectiveForDetail
                    ? 1100
                    : undefined
              } // InitiativeBoardWorkPopup(1100) 위에 표시, ObjectiveDetailPopup(1000) 위에 표시
              type={
                isOpeningStoryFromObjective
                  ? 'STORY' // ObjectiveDetailPopup에서 Initiative 클릭 시 STORY 타입
                  : selectedTask
                    ? activeLevel === 'MEMBER'
                      ? 'TASK'
                      : activeLevel === 'TEAM'
                        ? 'STORY'
                        : 'EPIC'
                    : createType
              }
              id={selectedTask?.worksInfoDto?.id || selectedTask?.id}
              createType={createType}
              task={selectedTask}
              selectedTeam={selectedTeam}
              parentStartDateTime={
                selectedTask?.taskDetails?.parentStartDateTime
              }
              parentEndDateTime={selectedTask?.taskDetails?.parentEndDateTime}
              fetchTodayData={fetchTodayData}
            />
          </>
        )}
        {createType === 'STORY' && (
          <InitiativeBoardWorkPopup
            selectedTask={selectedTask}
            weekOffset={0}
            createType={createType}
            handleLoadTaskDetail={handleLoadTaskDetail}
            isLast={false}
            onClose={() => {
              setIsInitiativePopupOpen(false)
              if (onInitiativePopupOpenChange)
                onInitiativePopupOpenChange(false)
              handleCancel()
            }}
            open={isModalOpen}
            onTaskClick={onTaskClick}
            zIndex={selectedObjectiveForDetail ? 1100 : 1100} // 항상 높은 z-index로 설정하여 다른 모달들 위에 표시
            onOpen={() => {
              setIsInitiativePopupOpen(true)
              if (onInitiativePopupOpenChange) onInitiativePopupOpenChange(true)
            }} // 열릴 때 플래그 설정
          />
        )}
        {renderWeekRow(thisWeek)}
        {renderWeekRow(nextWeek, true)}
      </div>

      {/* 2. 오른쪽 영역: Task 타임 테이블 */}
      {createType === 'TASK' && renderTaskTimeTable()}
    </div>
  )
}

export default ScrumBoard
